import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {
  Button,
  Card,
  Collapse,
  Menu,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CaretIcon from "../CaretIcon";
import { CartItem, LooseItem } from "../../types";
import { Add, Delete, Edit, MoreVert, Remove } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import RenderProduct from "../RenderProduct";
import { handleLinkToDetails } from "../../utils";
import AccessoryPlaceholder from "../../assets/images/accessory.png";
import NoImagePlaceholder from "../../assets/images/no-image_100.png";

function CartProductTableHead() {
  return (
    <TableHead>
      <TableRow sx={{ height: "50px" }}>
        <TableCell
          id="checkmarks"
          sx={{ paddingLeft: "32px", width: "80px" }}
        ></TableCell>
        <TableCell
          id="product-info"
          align={"left"}
          sx={{ paddingRight: "32px" }}
        >
          <Typography
            variant="bodyMediumWeightMedium"
            color="grey.700"
            sx={{ fontSize: { xs: "14px", md: "12px" } }}
          >
            Product
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

interface RowProps {
  selected: readonly string[];
  index: string;
  handleClick: (id: string) => void;
  handleEdit: (row: CartItem | LooseItem) => void;
  canEdit: boolean;
  handleUpdateQuantity: (id: string, quantity: number) => void;
  removeProduct: (id: string) => void;
  rowItem?: CartItem;
  rowLooseItem?: LooseItem;
}

function Row({
  rowItem,
  rowLooseItem,
  selected,
  index,
  handleUpdateQuantity,
  handleEdit,
  canEdit,
  removeProduct,
}: Readonly<RowProps>) {
  const [open, setOpen] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const theme = useTheme();
  const isResponsive = useMediaQuery(theme.breakpoints.down("md"));
  const isItemSelected = selected.includes(
    rowItem?.product.id ?? rowLooseItem?.id ?? "-1",
  );
  const labelId = `enhanced-table-checkbox-${index}`;

  const id = rowItem?.id ?? rowLooseItem?.id ?? "-1";
  const productId = rowItem?.product.id ?? rowLooseItem?.id ?? "-1";
  const name = rowItem?.product.name ?? rowLooseItem?.name ?? "";
  const count = rowItem?.configuration.count ?? rowLooseItem?.count ?? 0;

  const isAccessory =
    rowItem?.baseProduct.type === "Accessory" ||
    rowLooseItem?.type === "accessory";

  const imageUrl = isAccessory
    ? AccessoryPlaceholder
    : (rowItem?.product.imageUrl ??
      rowLooseItem?.imageUrl ??
      NoImagePlaceholder);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleEditClick = () => {
    if (!canEdit) {
      return;
    }
    handleCloseMenu();
    handleEdit((rowItem ?? rowLooseItem)!);
  };

  const handleRemoveClick = () => {
    handleCloseMenu();
    removeProduct(id);
  };

  const handleExpandClick = () => {
    handleCloseMenu();
    setOpen((prev) => !prev);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          cursor: "pointer",
          height: "81px",
        }}
        hover
        tabIndex={-1}
        key={id}
        selected={isItemSelected}
      >
        <TableCell
          align="center"
          sx={{ width: "105px", padding: 0, lineHeight: 0 }}
        >
          <RenderProduct src={imageUrl} name={name} />
        </TableCell>
        <TableCell headers="product-info" scope="row" align="left">
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography
              id={labelId}
              variant="bodyMediumWeightMedium"
              color="common.black"
            >
              {name}
            </Typography>
            <Button
              sx={(theme) => ({
                padding: 0,
                color: theme.palette.primary.main,
                justifyContent: "start",
              })}
              variant="text"
              onClick={() => handleLinkToDetails(productId)}
            >
              <Typography variant="bodyMediumWeightRegular">
                {productId}
              </Typography>
            </Button>
            {isResponsive && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  mt: "4px",
                }}
              >
                <Box sx={{ display: "flex", gap: "4px" }}>
                  <IconButton
                    aria-label="decrease quantity"
                    disabled={count <= 1}
                    onClick={() => handleUpdateQuantity(id, count - 1)}
                    size="small"
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    inputProps={{ "aria-label": "quantity" }}
                    sx={{ width: "50px", "& input": { textAlign: "center" } }}
                    value={count}
                    onChange={(e) =>
                      handleUpdateQuantity(id, Number(e.target.value))
                    }
                  />
                  <IconButton
                    aria-label="increase quantity"
                    onClick={() => handleUpdateQuantity(id, count + 1)}
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  {rowItem && rowItem.subProducts.length > 0 && (
                    <IconButton
                      sx={{ width: "32px", height: "32px", padding: 0 }}
                      aria-label="expand row"
                      onClick={() => setOpen(!open)}
                    >
                      <CaretIcon
                        customStyles={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                          margin: 0,
                        }}
                      />
                    </IconButton>
                  )}
                  <IconButton
                    aria-label="more actions"
                    onClick={handleOpenMenu}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={isMenuOpen}
                  onClose={handleCloseMenu}
                >
                  {canEdit && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  <MenuItem onClick={handleRemoveClick}>Delete</MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </TableCell>
        {!isResponsive && (
          <TableCell
            headers="product-info"
            align="left"
            sx={{ width: "105px" }}
          >
            <Box sx={{ display: "flex", gap: "4px" }}>
              <IconButton
                aria-label="decrease quantity"
                disabled={count <= 1}
                onClick={() => handleUpdateQuantity(id, count - 1)}
              >
                <Remove />
              </IconButton>
              <TextField
                inputProps={{ "aria-label": "quantity" }}
                sx={{ width: "50px", "& input": { textAlign: "center" } }}
                value={count}
                onChange={(e) =>
                  handleUpdateQuantity(id, Number(e.target.value))
                }
              />
              <IconButton
                aria-label="increase quantity"
                onClick={() => handleUpdateQuantity(id, count + 1)}
              >
                <Add />
              </IconButton>
            </Box>
          </TableCell>
        )}
        {!isResponsive && (
          <TableCell headers="product-info" sx={{ width: "80px" }}>
            <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <IconButton
                aria-label="remove product"
                onClick={handleRemoveClick}
              >
                <Delete />
              </IconButton>
              {canEdit && (
                <IconButton aria-label="edit product" onClick={handleEditClick}>
                  <Edit />
                </IconButton>
              )}
            </Box>
          </TableCell>
        )}
        {!isResponsive && (
          <TableCell
            headers="product-info"
            padding="none"
            sx={{ paddingRight: "32px", width: "80px" }}
          >
            {rowItem && rowItem.subProducts.length > 0 && (
              <IconButton
                sx={{ width: "32px", height: "32px", padding: 0 }}
                aria-label="expand row"
                onClick={() => setOpen(!open)}
              >
                <CaretIcon
                  customStyles={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                    margin: 0,
                  }}
                />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>
      {rowItem && (
        <TableRow>
          <TableCell
            style={{
              ...{ paddingBottom: 0, paddingTop: 0 },
              ...(open ? {} : { height: "0px" }),
            }}
            colSpan={isResponsive ? 2 : 6}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box>
                <Table size="small" aria-label="purchases">
                  <TableBody>
                    {rowItem.subProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell
                          padding="none"
                          sx={{ paddingLeft: "32px", width: "157" }}
                        />
                        <TableCell
                          align="center"
                          sx={{ width: "105px", padding: 0, lineHeight: 0 }}
                        >
                          <RenderProduct
                            src={AccessoryPlaceholder}
                            name={product.name}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <Typography
                              variant="bodyMediumWeightMedium"
                              color="common.black"
                            >
                              {product.name}
                            </Typography>
                            <Button
                              sx={(theme) => ({
                                padding: 0,
                                color: theme.palette.primary.main,
                                justifyContent: "start",
                              })}
                              variant="text"
                              onClick={() => handleLinkToDetails(product.id)}
                            >
                              <Typography
                                variant="bodyMediumWeightRegular"
                                color="grey.700"
                              >
                                {product.id}
                              </Typography>
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Typography
                            variant="bodyMediumWeightMedium"
                            color="common.black"
                          >
                            {product.quantity}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

interface CartProductTableProps {
  rowsItems: CartItem[];
  rowsLooseItems: LooseItem[];
  handleUpdateQuantity: (productId: string, quantity: number) => void;
  removeProduct: (productId: string) => void;
}

const CartProductTable = ({
  rowsItems,
  rowsLooseItems,
  handleUpdateQuantity,
  removeProduct,
}: CartProductTableProps) => {
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const navigate = useNavigate();

  const canEditItem = (item: CartItem | LooseItem): item is CartItem => {
    return "baseProduct" in item && "configuration" in item;
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = [
        ...rowsItems.map((item) => item.product.id),
        ...rowsLooseItems.map((item) => item.id),
      ];
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleEdit = (item: CartItem | LooseItem) => {
    if (!canEditItem(item)) {
      return;
    }

    navigate(`/configure/${item.baseProduct.id}/${item.categoryId}`, {
      state: {
        id: item.id,
        edit: item.configuration,
        isEdit: true,
      },
    });
  };

  if (rowsItems.length + rowsLooseItems.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "200px",
          p: 4,
          boxShadow: "none",
        }}
      >
        <Typography variant="h6" color="grey[600]">
          No items in cart
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ width: "100%", mb: 2, boxShadow: "none", padding: 0 }}
    >
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={"medium"}>
          <CartProductTableHead />
          <TableBody>
            {rowsItems.map((row) => {
              return (
                <Row
                  key={row.product.id}
                  index={row.product.id}
                  selected={selected}
                  handleClick={handleClick}
                  handleEdit={handleEdit}
                  canEdit={canEditItem(row)}
                  handleUpdateQuantity={handleUpdateQuantity}
                  removeProduct={removeProduct}
                  rowItem={row}
                />
              );
            })}
            {rowsLooseItems.map((row) => {
              return (
                <Row
                  key={row.id}
                  index={row.id}
                  selected={selected}
                  handleClick={handleClick}
                  handleEdit={handleEdit}
                  canEdit={canEditItem(row)}
                  handleUpdateQuantity={handleUpdateQuantity}
                  removeProduct={removeProduct}
                  rowLooseItem={row}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default CartProductTable;
